/**
 * Browser-side SSH keypair generation for SFTP onboarding.
 *
 * SECURITY: the PRIVATE key is generated here and is NEVER transmitted — only the
 * public key is sent to CommissionSight (via PUT /sftp/key). The caller hands the
 * private key to the user to download and load into their SFTP client.
 *
 * Produces an Ed25519 keypair: the public key as a one-line OpenSSH string
 * (`ssh-ed25519 AAAA… comment`) and the private key in OpenSSH format
 * (`-----BEGIN OPENSSH PRIVATE KEY-----`), which `ssh`, WinSCP, FileZilla, etc.
 * all accept. No external dependencies — uses WebCrypto + manual SSH wire encoding.
 */

const td = new TextEncoder();

function concat(...parts: Uint8Array[]): Uint8Array {
  const len = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function u32(n: number): Uint8Array {
  const b = new Uint8Array(4);
  new DataView(b.buffer).setUint32(0, n >>> 0, false); // big-endian
  return b;
}

/** SSH "string": 4-byte big-endian length prefix + bytes. */
function sshString(bytes: Uint8Array): Uint8Array {
  return concat(u32(bytes.length), bytes);
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] as number);
  return btoa(s);
}

/** Wrap base64 at 70 columns (OpenSSH PEM convention). */
function wrap70(b64: string): string {
  return (b64.match(/.{1,70}/g) ?? [b64]).join('\n');
}

const KEY_TYPE = 'ssh-ed25519';

/** Encode the OpenSSH public-key blob: string("ssh-ed25519") + string(pub32). */
function publicBlob(pub32: Uint8Array): Uint8Array {
  return concat(sshString(td.encode(KEY_TYPE)), sshString(pub32));
}

/** One-line OpenSSH public key: `ssh-ed25519 <base64(blob)> <comment>`. */
export function encodeOpenSshPublicKey(pub32: Uint8Array, comment: string): string {
  return `${KEY_TYPE} ${toBase64(publicBlob(pub32))} ${comment}`.trim();
}

/**
 * Encode an unencrypted OpenSSH private key file. `seed32` is the Ed25519 private
 * seed, `pub32` the raw public key (priv field is seed||pub = 64 bytes).
 */
export function encodeOpenSshPrivateKey(
  seed32: Uint8Array,
  pub32: Uint8Array,
  comment: string,
): string {
  const checkint = new Uint8Array(4);
  crypto.getRandomValues(checkint);
  const priv64 = concat(seed32, pub32);

  let unencrypted = concat(
    checkint,
    checkint,
    sshString(td.encode(KEY_TYPE)),
    sshString(pub32),
    sshString(priv64),
    sshString(td.encode(comment)),
  );
  // Pad with 1,2,3,… to a multiple of the cipher block size (8 for "none").
  for (let pad = 1; unencrypted.length % 8 !== 0; pad++) {
    unencrypted = concat(unencrypted, new Uint8Array([pad]));
  }

  const body = concat(
    td.encode('openssh-key-v1\0'),
    sshString(td.encode('none')), // ciphername
    sshString(td.encode('none')), // kdfname
    sshString(new Uint8Array(0)), // kdfoptions
    u32(1), // number of keys
    sshString(publicBlob(pub32)),
    sshString(unencrypted),
  );

  return `-----BEGIN OPENSSH PRIVATE KEY-----\n${wrap70(toBase64(body))}\n-----END OPENSSH PRIVATE KEY-----\n`;
}

export interface SshKeypair {
  /** One-line OpenSSH public key to register with the server. */
  publicKey: string;
  /** OpenSSH-format private key the user downloads and keeps. NEVER sent anywhere. */
  privateKey: string;
}

/** True if this browser can generate Ed25519 keys via WebCrypto. */
export async function canGenerateSshKey(): Promise<boolean> {
  try {
    await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
    return true;
  } catch {
    return false;
  }
}

/**
 * Generate an Ed25519 SSH keypair entirely in the browser. The private key never
 * leaves this function's caller — only `publicKey` should be sent to the server.
 */
export async function generateSshKeypair(comment = 'commissionsight-sftp'): Promise<SshKeypair> {
  const pair = (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, [
    'sign',
    'verify',
  ])) as CryptoKeyPair;
  const pub32 = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
  // pkcs8 Ed25519: the 32-byte seed is the trailing OCTET STRING of the DER.
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey));
  const seed32 = pkcs8.slice(pkcs8.length - 32);
  return {
    publicKey: encodeOpenSshPublicKey(pub32, comment),
    privateKey: encodeOpenSshPrivateKey(seed32, pub32, comment),
  };
}
